import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Send, AudioLines, Loader2 } from "lucide-react";
import { useGoogleFont } from "../hooks/useGoogleFont.js";
import { cn } from "../lib/utils.js";

const ASSETS = {
  BRAND: {
    LOGOS: {
      FULL: {
        LIGHT: "https://app.eshal.ai/brand/logos/full/logo-full-light.svg",
      },
    },
  },
};

const ChatInput = ({
  inputValue,
  setInputValue,
  onSend,
  isDark,
  primaryColor,
  isOpen,
  isMinimized,
  isLoading,
  placeholder,
  textColor,
  fontFamily,
  fontSize,
  isRtl = false,
  enableVoiceInteraction = false,
  isVoiceSessionActive = false,
  voiceStatus = "idle",
  voiceError = null,
  onVoiceToggle,
  onBidiSubmit,
  disclaimerText,
  disclaimerPosition = "top",
  showPoweredBy = true,
  onboardingQuestions = [],
  onboardingActive = false,
  onboardingCompleted = false,
  userMessageBubbleFontFamily,
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({
    top: 0,
    left: 0,
    placement: "top",
    arrowOffset: 0,
  });
  const [logoImageError, setLogoImageError] = useState(false);
  const tooltipTimeoutRef = useRef(null);
  const textareaRef = useRef(null);
  const voiceButtonRef = useRef(null);
  const tooltipRef = useRef(null);
  useGoogleFont(fontFamily);
  useGoogleFont(userMessageBubbleFontFamily);

  useEffect(() => {
    if (isOpen && !isMinimized) {
      textareaRef.current?.focus();
    }
  }, [isOpen, isMinimized]);

  // Auto-resize textarea
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      // Reset height to auto to get accurate scrollHeight
      textarea.style.height = "auto";
      // Calculate new height based on scrollHeight
      const scrollHeight = textarea.scrollHeight;
      // Ensure minimum height when there's text (lineHeight 1.4 means we need at least ~20px per line)
      const minHeightWithText = inputValue.trim().length > 0 ? 20 : 48;
      const newHeight = Math.max(
        minHeightWithText,
        Math.min(scrollHeight, 120)
      );
      textarea.style.height = `${newHeight}px`;
    }
  }, [inputValue]);

  const handleKeyPress = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      if (isVoiceSessionActive && onBidiSubmit) {
        onBidiSubmit();
      } else {
        onSend();
      }
    }
  };

  const handleChange = (event) => {
    setInputValue(event.target.value);
  };

  const isDisabled = !inputValue.trim() || isLoading;

  const separatorColor = isDark ? "#374151" : "#e5e7eb";

  // Check if onboarding flow exists and is not completed
  const hasOnboardingQuestions =
    onboardingQuestions && onboardingQuestions.length > 0;
  const isOnboardingIncomplete = hasOnboardingQuestions && !onboardingCompleted;
  const isVoiceButtonDisabled =
    isOnboardingIncomplete || voiceStatus === "connecting";

  // Determine tooltip message
  const getTooltipMessage = () => {
    if (!hasOnboardingQuestions) {
      return "";
    }
    if (onboardingActive) {
      return "Complete the onboarding to enable voice";
    }
    if (!onboardingCompleted) {
      return "Send a message to complete onboarding and enable voice.";
    }
    return "";
  };

  const tooltipMessage = getTooltipMessage();

  // Calculate tooltip position dynamically
  const calculateTooltipPosition = () => {
    if (!voiceButtonRef.current) return;

    const buttonRect = voiceButtonRef.current.getBoundingClientRect();
    const tooltipWidth = 220; // maxWidth from style
    const tooltipHeight = 60; // approximate height
    const arrowHeight = 5; // height of the arrow triangle
    const spacing = 8; // spacing between button and tooltip (between arrow tip and button)

    // Try to position above first (preferred)
    const spaceAbove = buttonRect.top;
    const spaceBelow = window.innerHeight - buttonRect.bottom;

    let placement = "top";
    let top = 0;
    let left = 0;

    if (spaceAbove >= tooltipHeight + arrowHeight + spacing) {
      // Position above
      placement = "top";
      top = buttonRect.top - tooltipHeight - arrowHeight - spacing;
      left = buttonRect.right - tooltipWidth; // Right-align with button

      // Ensure tooltip doesn't go off-screen to the left
      if (left < 10) {
        left = 10;
      }
      // Ensure tooltip doesn't go off-screen to the right
      if (left + tooltipWidth > window.innerWidth - 10) {
        left = window.innerWidth - tooltipWidth - 10;
      }
    } else if (spaceBelow >= tooltipHeight + spacing) {
      // Position below as fallback
      placement = "bottom";
      top = buttonRect.bottom + spacing;
      left = buttonRect.right - tooltipWidth;

      // Ensure tooltip doesn't go off-screen
      if (left < 10) {
        left = 10;
      }
      if (left + tooltipWidth > window.innerWidth - 10) {
        left = window.innerWidth - tooltipWidth - 10;
      }
    } else {
      // Not enough space above or below, position above anyway (may be partially visible)
      placement = "top";
      top = Math.max(
        10,
        buttonRect.top - tooltipHeight - arrowHeight - spacing
      );
      left = buttonRect.right - tooltipWidth;

      if (left < 10) {
        left = 10;
      }
      if (left + tooltipWidth > window.innerWidth - 10) {
        left = window.innerWidth - tooltipWidth - 10;
      }
    }

    // Calculate arrow offset (center of button relative to tooltip left edge)
    const buttonCenterX = buttonRect.left + buttonRect.width / 2;
    const arrowOffset = buttonCenterX - left;

    return { top, left, placement, arrowOffset };
  };

  // Handle tooltip show/hide
  const handleMouseEnter = () => {
    if (isVoiceButtonDisabled && tooltipMessage) {
      if (tooltipTimeoutRef.current) {
        clearTimeout(tooltipTimeoutRef.current);
        tooltipTimeoutRef.current = null;
      }
      // Calculate position and show tooltip
      const position = calculateTooltipPosition();
      setTooltipPosition(position);
      setShowTooltip(true);
    }
  };

  const handleMouseLeave = () => {
    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current);
      tooltipTimeoutRef.current = null;
    }
    setShowTooltip(false);
  };

  // Update tooltip position on scroll/resize
  useEffect(() => {
    if (showTooltip && tooltipMessage) {
      const updatePosition = () => {
        const position = calculateTooltipPosition();
        setTooltipPosition(position);
      };

      window.addEventListener("scroll", updatePosition, true);
      window.addEventListener("resize", updatePosition);

      return () => {
        window.removeEventListener("scroll", updatePosition, true);
        window.removeEventListener("resize", updatePosition);
      };
    }
  }, [showTooltip, tooltipMessage]);

  useEffect(() => {
    return () => {
      if (tooltipTimeoutRef.current) {
        clearTimeout(tooltipTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div
      className={`px-5 py-4 flex-shrink-0 relative ${isDark ? "bg-gray-900" : "bg-white"
        }`}
      style={{
        overflow: "visible",
      }}
    >
      <div className="flex items-center gap-1.5">
        <div
          className={`flex-1 flex items-center min-h-[48px] ${isDark ? "bg-gray-800" : "bg-gray-50"
            } rounded-2xl transition-all duration-200`}
          style={{
            borderStyle: "solid",
            borderColor: inputValue && inputValue.trim().length > 0
              ? (primaryColor || "#2563eb")
              : "transparent",
            borderWidth: "2px",
          }}
        >
          <textarea
            id="chat-widget-input"
            ref={textareaRef}
            value={inputValue}
            onChange={handleChange}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            onKeyDown={handleKeyPress}
            placeholder={placeholder || "Type your message..."}
            rows={1}
            disabled={
              isLoading || (isVoiceSessionActive && voiceStatus !== "connected")
            }
            dir={isRtl ? "rtl" : "ltr"}
            className={`w-full px-4 bg-transparent rounded-xl leading-normal ${isDark ? "text-gray-100" : "text-gray-900"
              } placeholder-gray-400 resize-none outline-none scrollbar-hide`}
            style={{
              height: "auto",
              maxHeight: "120px",
              minHeight: "48px",
              paddingTop: inputValue.trim().length > 0 ? "12px" : "0px",
              paddingBottom: inputValue.trim().length > 0 ? "12px" : "0px",
              lineHeight: inputValue.trim().length > 0 ? "1.4" : "48px",
              fontFamily: userMessageBubbleFontFamily || fontFamily || "Inter",
              fontSize: "14px",
              color: isDark ? "#FFFFFF" : "#000000",
              textAlign: isRtl ? "right" : "left",
              direction: isRtl ? "rtl" : "ltr",
              wordSpacing: "0",
              letterSpacing: "0",
            }}
          />
        </div>
        {enableVoiceInteraction && (
          <div
            className="relative"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <button
              style={{
                border: "none",
              }}
              ref={voiceButtonRef}
              type="button"
              onClick={onVoiceToggle}
              className={`h-10 w-10 rounded-xl flex items-center justify-center transition-all duration-200 shadow-md ${isVoiceSessionActive
                ? "bg-red-500 text-white hover:bg-red-600"
                : isDark
                  ? "bg-gray-800 text-gray-200 hover:bg-gray-700"
                  : "bg-white text-gray-600 hover:bg-gray-100"
                } ${isVoiceButtonDisabled
                  ? "opacity-60 cursor-not-allowed shadow-none"
                  : ""
                }`}
              disabled={isVoiceButtonDisabled}
            >
              {voiceStatus === "connecting" ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <AudioLines className="w-4 h-4" />
              )}
            </button>
            {showTooltip &&
              tooltipMessage &&
              typeof document !== "undefined" &&
              createPortal(
                <div
                  ref={tooltipRef}
                  className="px-3 py-1.5 text-xs shadow-md pointer-events-none"
                  style={{
                    position: "fixed",
                    top: `${tooltipPosition.top}px`,
                    left: `${tooltipPosition.left}px`,
                    maxWidth: "220px",
                    whiteSpace: "normal",
                    wordWrap: "break-word",
                    minWidth: "180px",
                    zIndex: 999999,
                    backgroundColor: isDark ? "#1f2937" : "#ffffff",
                    color: isDark ? "#f3f4f6" : "#111827",
                    border: isDark ? "1px solid #374151" : "1px solid #e5e7eb",
                    borderRadius:
                      tooltipPosition.placement === "top"
                        ? "6px 6px 6px 6px"
                        : "6px",
                    boxShadow:
                      "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
                    overflow: "visible",
                  }}
                >
                  <div className="text-left leading-relaxed">
                    {tooltipMessage}
                  </div>
                  {/* Arrow - positioned based on placement */}
                  {tooltipPosition.placement === "top" && (
                    <>
                      {/* Arrow - border layer (extends outside tooltip, pointing down) */}
                      <div
                        style={{
                          position: "absolute",
                          top: "100%",
                          left: `${tooltipPosition.arrowOffset}px`,
                          transform: "translateX(-50%)",
                          width: 0,
                          height: 0,
                          borderLeft: "6px solid transparent",
                          borderRight: "6px solid transparent",
                          borderTop: isDark
                            ? "6px solid #374151"
                            : "6px solid #e5e7eb",
                          pointerEvents: "none",
                        }}
                      />
                      {/* Arrow - fill layer (extends outside tooltip, slightly smaller to show border) */}
                      <div
                        style={{
                          position: "absolute",
                          top: "100%",
                          left: `${tooltipPosition.arrowOffset}px`,
                          transform: "translateX(-50%)",
                          marginTop: "-1px",
                          width: 0,
                          height: 0,
                          borderLeft: "5px solid transparent",
                          borderRight: "5px solid transparent",
                          borderTop: isDark
                            ? "5px solid #1f2937"
                            : "5px solid #ffffff",
                          pointerEvents: "none",
                        }}
                      />
                    </>
                  )}
                  {tooltipPosition.placement === "bottom" && (
                    <>
                      {/* Arrow - border layer */}
                      <div
                        className="absolute bottom-full"
                        style={{
                          left: `${tooltipPosition.arrowOffset}px`,
                          transform: "translateX(-50%)",
                          width: 0,
                          height: 0,
                          borderLeft: "5px solid transparent",
                          borderRight: "5px solid transparent",
                          borderBottom: isDark
                            ? "5px solid #374151"
                            : "5px solid #e5e7eb",
                        }}
                      />
                      {/* Arrow - fill layer */}
                      <div
                        className="absolute bottom-full -mb-px"
                        style={{
                          left: `${tooltipPosition.arrowOffset}px`,
                          transform: "translateX(-50%)",
                          width: 0,
                          height: 0,
                          borderLeft: "4px solid transparent",
                          borderRight: "4px solid transparent",
                          borderBottom: isDark
                            ? "4px solid #1f2937"
                            : "4px solid #ffffff",
                        }}
                      />
                    </>
                  )}
                </div>,
                document.body
              )}
          </div>
        )}
        <button
          onClick={isVoiceSessionActive && onBidiSubmit ? onBidiSubmit : onSend}
          disabled={
            isDisabled || (isVoiceSessionActive && voiceStatus !== "connected")
          }
          className={`h-10 w-10 rounded-xl transition-all duration-200 flex items-center justify-center shadow-md disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none ${inputValue.trim() ? "scale-100" : "scale-95"
            }`}
          style={{
            backgroundColor: inputValue.trim()
              ? primaryColor
              : isDark
                ? "#374151"
                : "#e5e7eb",
            color: inputValue.trim() ? "white" : "#9ca3af",
            border: "none",
          }}
          aria-label="Send message"
        >
          <Send
            className="w-4 h-4 transition-transform"
            style={isRtl ? { transform: "rotate(-90deg)" } : undefined}
          />
        </button>
      </div>
      {isVoiceSessionActive && (
        <p
          className={`text-[11px] mt-2 px-1 ${voiceStatus === "connected" ? "text-green-500" : "text-gray-500"
            } flex items-center gap-1`}
        >
          <span className="w-1.5 h-1.5 rounded-full bg-current inline-flex"></span>
          {voiceStatus === "connected"
            ? "Voice session live — speak or type to converse."
            : "Connecting to voice session..."}
        </p>
      )}
      {voiceError && (
        <p className="text-[11px] mt-1 px-1 text-red-500">{voiceError}</p>
      )}
      {/* Disclaimer at Footer */}
      {disclaimerText && disclaimerPosition === "footer" && (
        <p
          className="text-xs mt-2 text-center"
          style={{
            color: isDark ? "#FFFFFF" : "#000000",
            fontFamily: fontFamily || "Inter",
            fontSize: fontSize ? `calc(${fontSize} - 2px)` : "12px",
            direction: isRtl ? "rtl" : "ltr",
          }}
          dir={isRtl ? "rtl" : "ltr"}
        >
          {disclaimerText}
        </p>
      )}
      {/* Powered by ESHAL */}
      {showPoweredBy && (
        <div
          className="mt-1.5 flex justify-center"
          dir={isRtl ? "rtl" : "ltr"}
        >
          <span
            className={cn(
              "inline-flex items-center gap-1.5 rounded-lg py-1.5 px-3 transition-colors",
              isDark
                ? "bg-gray-800/50 border border-gray-700/50"
                : "bg-gray-100/80 border border-gray-200/50"
            )}
          >
            <span
              className={cn(
                "font-sans text-[10px] font-medium",
                isDark ? "text-gray-400" : "text-gray-500"
              )}
            >
              Powered by
            </span>
            {!logoImageError ? (
              <img
                src={ASSETS.BRAND.LOGOS.FULL.LIGHT}
                alt="ESHAL"
                className={cn(
                  "h-3",
                  isDark ? "brightness-0 invert opacity-60" : "brightness-0 opacity-50"
                )}
                onError={() => setLogoImageError(true)}
              />
            ) : (
              <span
                className={cn(
                  "font-sans text-[10px] font-semibold",
                  isDark ? "text-gray-300" : "text-gray-600"
                )}
              >
                ESHAL
              </span>
            )}
          </span>
        </div>
      )}
    </div>
  );
};

export default ChatInput;
